// src/pages/Gallery.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";

/**
 * List your gallery media here.
 * - file: exact filename inside the folder
 * - caption: what customers see under the photo/video
 *
 * Folders:
 *   public/assets/gallery/wheels
 *   public/assets/gallery/exhaust
 */
const WHEEL_MEDIA = [
  { file: "2piece.mp4", caption: "M2 — Brushed Silver/Gloss Clearcoat" },
  { file: "3piece.mp4", caption: "M1 — Brushed Royal Gold/Gloss Clearcoat" },
  { file: "monoblock.mp4", caption: "M3 - Polished Champagne faces with Brushed Barrels" },
  { file: "r8-m3-wheels.jpg", caption: "M3 - Satin Black" },
  { file: "M7studio.mp4", caption: "M7 - Brushed Champagne-Polished Lip-Brushed Barrels" },
  { file: "cybertruck.mp4", caption: "M8 - Gloss Black-Floating Center Caps 24's" },
  { file: "m9bmw7.jpg", caption: "M9 - Brushed Silver 20's" },
  { file: "20250816_183547.jpg", caption: "M6 - Polished w/Matte Clearcoat 21's" },
  { file: "03.mov", caption: "M3 - Polished Champagne faces with Brushed Barrels 22's" },
  { file: "_DSC1350.jpg", caption: "M1 - Brushed Royal Gold/Gloss Clearcoat 21's" },
  { file: "_DSC1351.jpg", caption: "M1 - Brushed Royal Gold/Gloss Clearcoat 21's" },
  { file: "DSC1357.jpg", caption: "M1 - Brushed Royal Gold/Gloss Clearcoat 21's" },
  { file: "_DSC1365.jpg", caption: "M1 - Brushed Royal Gold/Gloss Clearcoat 21's" },
  { file: "_DSC1367.jpg", caption: "M1 - Brushed Royal Gold/Gloss Clearcoat 21's" },
  { file: "DSC1358.jpg", caption: "M1 - Brushed Royal Gold/Gloss Clearcoat 21's" },
  { file: "DSC1373.jpg", caption: "M1 - Brushed Royal Gold/Gloss Clearcoat 21's" },
  { file: "m9bmw7side.jpg", caption: "M9 - Brushed Silver 21's" },
  { file: "DSC1358a.jpg", caption: "M1 - Satin Black 21's" },
  { file: "DSC1358b.jpg", caption: "M1 - Brushed Silver 21's" },
  { file: "m7reel1.mp4", caption: "M7 - Brushed Champagne-Polished Lip-Brushed Barrels" },
  { file: "m7reel2.mp4", caption: "M7 - Brushed Champagne-Polished Lip-Brushed Barrels" },
  { file: "2O9A3312.JPG", caption: "M7 - Brushed Champagne-Polished Lip-Brushed Barrels" },
  { file: "2O9A3311.JPG", caption: "M7 - Brushed Champagne-Polished Lip-Brushed Barrels" },
  { file: "2O9A3315.JPG", caption: "M7 - Brushed Champagne-Polished Lip-Brushed Barrels" },
  { file: "m6amg.jpg", caption: "M6 - Polished w/Matte Clearcoat 21's" },
  { file: "m6amggts.jpg", caption: "M6 - Polished w/Matte Clearcoat 21's" },
  { file: "m2x3.jpg", caption: "M2 — Brushed Silver/Gloss Clearcoat" },
  { file: "m8cybertruck.jpg", caption: "M8 - Gloss Black-Floating Center Caps 24's" },
  { file: "m8cybertruckk.jpg", caption: "M8 - Gloss Black-Floating Center Caps 24's" },
  { file: "m8cybertruckkk.jpg", caption: "M8 - Gloss Black-Floating Center Caps 24's" },












];

const EXHAUST_MEDIA = [
 
   { file: "titanium.mp4", caption: "BMW M5 F90 – Gold Titanium Catback w/Valves" },
   { file: "exh_clip02.mp4", caption: "BMW F90 M5 – Titanium - Gold Valved Exhaust" },
   { file: "exh_clip01.mp4", caption: "BMW F90 M5 – Titanium - Gold Valved Exhaust" },
];


/** Decide if a file is an image or video */
function getKind(filename) {
  return /\.(mp4|webm|mov|m4v)$/i.test(filename) ? "video" : "image";
}

/** Fallback caption: "m7-brushed-champagne-front.jpg" -> "m7 brushed champagne front" */
function filenameToLabel(filename) {
  const withoutExt = filename.replace(/\.[^/.]+$/, "");
  return withoutExt.replace(/[-_]+/g, " ");
}

/** Simple shuffle so initial gallery is a random mix */
function shuffleArray(list) {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function Gallery() {
  // "", "wheels", "exhaust"
  const [filter, setFilter] = useState("");

  // lightbox state: index in visibleMedia, or null if closed
  const [activeIndex, setActiveIndex] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);

  const allMedia = useMemo(() => {
    const wheels = WHEEL_MEDIA.map((item, index) => {
      const file = item.file;
      return {
        id: `wheel-${index}-${file}`,
        type: "wheels",
        kind: getKind(file),
        src: `/assets/gallery/wheels/${file}`,
        label: item.caption || filenameToLabel(file),
      };
    });

    const exhaust = EXHAUST_MEDIA.map((item, index) => {
      const file = item.file;
      return {
        id: `exhaust-${index}-${file}`,
        type: "exhaust",
        kind: getKind(file),
        src: `/assets/gallery/exhaust/${file}`,
        label: item.caption || filenameToLabel(file),
      };
    });

    return shuffleArray([...wheels, ...exhaust]);
  }, []);

  const visibleMedia = useMemo(() => {
    if (filter === "wheels") return allMedia.filter((m) => m.type === "wheels");
    if (filter === "exhaust")
      return allMedia.filter((m) => m.type === "exhaust");
    return allMedia;
  }, [filter, allMedia]);

  const isLightboxOpen = activeIndex !== null;
  const current =
    isLightboxOpen && activeIndex >= 0 && activeIndex < visibleMedia.length
      ? visibleMedia[activeIndex]
      : null;

  // Close lightbox whenever filter changes (so index doesn't mismatch)
  useEffect(() => {
    setActiveIndex(null);
  }, [filter]);

  // Autoplay video in lightbox, try with sound first then fall back to muted
  useEffect(() => {
    if (!current || current.kind !== "video" || !videoRef.current) return;

    const v = videoRef.current;
    v.currentTime = 0;
    v.muted = isMuted;

    const playPromise = v.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise.catch(() => {
        // If autoplay with sound is blocked, force muted autoplay
        v.muted = true;
        setIsMuted(true);
        v.play().catch(() => {});
      });
    }
  }, [current, isMuted]);

  // Keyboard controls: Esc to close, arrows to navigate
  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKey = (e) => {
      if (e.key === "Escape") {
        setActiveIndex(null);
      } else if (e.key === "ArrowRight") {
        setActiveIndex((prev) =>
          prev === null
            ? 0
            : (prev + 1) % (visibleMedia.length || 1)
        );
      } else if (e.key === "ArrowLeft") {
        setActiveIndex((prev) =>
          prev === null
            ? 0
            : (prev - 1 + (visibleMedia.length || 1)) %
              (visibleMedia.length || 1)
        );
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isLightboxOpen, visibleMedia.length]);

  const openLightboxAt = (index) => {
    setActiveIndex(index);
    setIsMuted(false); // try with sound first
  };

  const goPrev = () => {
    if (!visibleMedia.length) return;
    setActiveIndex((prev) =>
      prev === null ? 0 : (prev - 1 + visibleMedia.length) % visibleMedia.length
    );
  };

  const goNext = () => {
    if (!visibleMedia.length) return;
    setActiveIndex((prev) =>
      prev === null ? 0 : (prev + 1) % visibleMedia.length
    );
  };

  return (
    <div className="min-h-screen bg-white text-black px-4 py-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-wide">
            GALLERY
          </h1>
          <p className="mt-2 text-sm md:text-base text-gray-500">
            View Unlimited Levels in all its beauty.
          </p>
        </div>

        {/* Dropdown: All / Wheels / Exhaust */}
        <div className="flex justify-center mb-8">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-60 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black bg-white"
          >
            <option value="">All media (Wheels &amp; Exhaust)</option>
            <option value="wheels">Wheels only</option>
            <option value="exhaust">Exhaust only</option>
          </select>
        </div>

        {/* Grid */}
        {visibleMedia.length === 0 ? (
          <p className="text-center text-gray-500 text-sm">
            No media found. Add items to WHEEL_MEDIA / EXHAUST_MEDIA in
            <code> Gallery.jsx</code>.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleMedia.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => openLightboxAt(index)}
                className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden text-left hover:shadow-md transition-shadow"
              >
                <div className="w-full aspect-[4/3] bg-gray-50">
                  {item.kind === "video" ? (
                    <video
                      src={item.src}
                      className="w-full h-full object-contain"
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img
                      src={item.src}
                      alt={item.label}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="px-4 py-3 text-xs text-gray-700 truncate">
                  {item.label}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox overlay */}
      {isLightboxOpen && current && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
          {/* Close button */}
          <button
            type="button"
            onClick={() => setActiveIndex(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl font-light"
          >
            ×
          </button>

          {/* Prev / Next arrows */}
          {visibleMedia.length > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-4 md:left-8 text-white/80 hover:text-white text-3xl"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-4 md:right-8 text-white/80 hover:text-white text-3xl"
              >
                ›
              </button>
            </>
          )}

          {/* Media area */}
          <div className="max-w-6xl w-full px-4">
            <div className="w-full flex items-center justify-center">
              {current.kind === "video" ? (
                <video
                  ref={videoRef}
                  src={current.src}
                  className="max-h-[80vh] w-auto object-contain"
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <img
                  src={current.src}
                  alt={current.label}
                  className="max-h-[80vh] w-auto object-contain"
                />
              )}
            </div>

            {/* Caption + mute toggle */}
            <div className="mt-4 flex items-center justify-center gap-4 text-xs text-white/80">
              <span className="truncate max-w-[70vw] text-center">
                {current.label}
              </span>
              {current.kind === "video" && (
                <button
                  type="button"
                  onClick={() => setIsMuted((m) => !m)}
                  className="border border-white/60 rounded-full px-3 py-1 text-[11px] uppercase tracking-wide hover:bg-white/10"
                >
                  {isMuted ? "Unmute" : "Mute"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;
